import React from 'react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { useBrandData } from '../../hooks/useBrandData';

export default function Typography() {
  const { data, loading, error } = useBrandData();

  if (loading) {
    return <div className="text-subtle" data-testid="typography-loading">Loading brand data…</div>;
  }
  if (error || !data) {
    return <div className="text-destructive" data-testid="typography-error">Failed to load brand data.</div>;
  }

  const { typography } = data;
  const { fonts, google_fonts_url, hierarchy, rules } = typography;

  return (
    <div className="space-y-8" data-testid="typography-section">
      <div>
        <h1 className="text-4xl md:text-5xl font-display font-normal text-jackstone-blue mb-4">
          Typography
        </h1>
        <p className="text-lg text-body leading-relaxed">
          Our typography system balances technical precision with modern readability.
        </p>
      </div>

      <Card className="p-8 bg-surface">
        <h2 className="text-3xl font-display font-normal text-body mb-3 uppercase">
          {fonts.display.family}
        </h2>
        <p className="text-body mb-4">
          Primary font for major headlines and display text
        </p>
        <div className="bg-background p-6 rounded-lg space-y-3">
          <div className="font-display text-4xl font-normal text-jackstone-blue uppercase">ABCDEFGHIJKLM</div>
          <div className="font-display text-4xl font-normal text-jackstone-blue uppercase">NOPQRSTUVWXYZ</div>
          <div className="font-display text-3xl font-normal text-subtle">0123456789</div>
        </div>
      </Card>

      <Card className="p-8">
        <h2 className="text-3xl font-heading font-semibold text-body mb-3">
          {fonts.body.family}
        </h2>
        <p className="text-body mb-4">
          Primary font for sub-headings and body text
        </p>
        <div className="bg-surface p-6 rounded-lg space-y-3">
          <div className="font-heading text-3xl font-semibold text-body">ABCDEFGHIJKLMNOPQRSTUVWXYZ</div>
          <div className="font-sans text-2xl text-subtle">abcdefghijklmnopqrstuvwxyz</div>
          <div className="font-sans text-xl text-subtle">0123456789 !@#$%^&amp;*()</div>
        </div>
      </Card>

      <div>
        <h2 className="text-2xl font-heading font-semibold text-jackstone-blue mb-4">
          Type Hierarchy
        </h2>
        <div className="space-y-6">
          {hierarchy.map((h) => (
            <Card key={h.role} className="p-6" data-testid={`hierarchy-${h.role.replace(/[^a-z0-9]/gi, '-').toLowerCase()}`}>
              <div className="mb-2">
                <Badge tone="jackstone-blue" fill="soft">{h.role}</Badge>
              </div>
              {h.role.startsWith('H1') && (
                <h1 className="text-5xl font-display font-normal text-jackstone-blue mb-2 uppercase">
                  MAIN HEADING
                </h1>
              )}
              {h.role.startsWith('H2') && (
                <h2 className="text-4xl font-heading font-semibold text-jackstone-blue mb-2">
                  Section Heading
                </h2>
              )}
              {h.role.startsWith('H3') && (
                <h3 className="text-2xl font-heading font-semibold text-body mb-2">
                  Subsection Heading
                </h3>
              )}
              {h.role === 'Body' && (
                <p className="text-base text-body mb-2 leading-relaxed font-medium">
                  Body text provides detailed information and should be highly readable.
                  Use Montserrat Medium for all body copy to ensure maximum legibility.
                </p>
              )}
              {h.role.startsWith('Caption') && (
                <p className="text-sm text-subtle mb-2">
                  Small text for captions, labels, and secondary information
                </p>
              )}
              <p className="text-sm text-subtle font-mono">
                {h.font} {h.weight} · {h.size} · Line height {h.line_height} · {h.casing}
              </p>
            </Card>
          ))}
        </div>
      </div>

      <Card className="p-6">
        <h3 className="text-xl font-heading font-semibold text-jackstone-blue mb-4">
          Font Weight Guidelines
        </h3>
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <h4 className="font-heading font-semibold text-body mb-3">{fonts.display.family}</h4>
            <div className="space-y-2">
              {fonts.display.weights.map((w) => (
                <div key={`d-${w}`} className="flex items-center justify-between p-3 bg-surface rounded">
                  <span className="font-display font-normal uppercase">Regular ({w})</span>
                  <span className="text-sm text-subtle">Major headings</span>
                </div>
              ))}
            </div>
          </div>
          <div>
            <h4 className="font-heading font-semibold text-body mb-3">{fonts.body.family}</h4>
            <div className="space-y-2">
              {fonts.body.weights.map((w) => {
                const label = w === 500 ? 'Medium' : w === 600 ? 'SemiBold' : w === 700 ? 'Bold' : `Weight ${w}`;
                const usage = w === 500 ? 'Body text' : w === 600 ? 'Subheadings' : 'Strong emphasis';
                return (
                  <div key={`b-${w}`} className="flex items-center justify-between p-3 bg-surface rounded">
                    <span className="font-heading font-semibold">{label} ({w})</span>
                    <span className="text-sm text-subtle">{usage}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </Card>

      <Card className="p-6">
        <h3 className="text-xl font-heading font-semibold text-jackstone-blue mb-4">
          Typography Rules
        </h3>
        <ul className="space-y-2 text-sm text-body">
          {rules.map((r) => (
            <li key={r} className="flex gap-2">
              <span className="text-jackstone-blue">•</span>
              <span>{r}</span>
            </li>
          ))}
        </ul>
      </Card>

      <Card className="p-6 bg-light-blue border-jackstone-blue/20">
        <h3 className="text-xl font-heading font-semibold text-body mb-3">
          Web Font Loading
        </h3>
        <p className="text-sm text-subtle mb-3">
          Load fonts via Google Fonts with display=swap for optimal performance:
        </p>
        <div className="bg-background p-4 rounded font-mono text-xs overflow-x-auto">
          <code className="text-jackstone-blue">
            {google_fonts_url}
          </code>
        </div>
      </Card>
    </div>
  );
}
